import React, { useState, useMemo } from "react";
import {
    Paper, Table, TableHead, TableBody, TableRow, TableCell,
    TableContainer, TablePagination, Typography, IconButton,
    Box
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { useTranslation } from 'react-i18next';
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

function GenericTable({
    data = [],
    columns = [],
    title = "My Table",
    noDataMessage = "No data",
    filterFn,
    height = "500px",
    rowHeight = "40px",
    onRowClick,
    onDropStatus,
    tableRef,
    clickedTable,
    handleChangeClickedTable,
    whatTable,
    defaultSort = { key: "", direction: "ascending" },
    defaultRowsPerPage = 100,
    rowsPerPageOptions = [5, 10, 25, 50, 100],
}) {
    const { t, i18n } = useTranslation();

    /* ---------- filtering ---------- */
    const filteredData = useMemo(
        () => (filterFn ? data.filter(filterFn) : data),
        [data, filterFn]
    );

    /* ---------- sorting ---------- */
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const sortedData = useMemo(() => {
        const arr = [...filteredData];
        const { key, direction } = sortConfig;
        if (!key) return arr;

        arr.sort((a, b) => {
            const aVal = a[key];
            const bVal = b[key];
            if (typeof aVal === "string") {
                return direction === "ascending"
                    ? aVal?.localeCompare(bVal)
                    : bVal?.localeCompare(aVal);
            }
            if (typeof aVal === "number") {
                return direction === "ascending" ? aVal - bVal : bVal - aVal;
            }
            return 0;
        });
        return arr;
    }, [filteredData, sortConfig]);

    /* ---------- pagination ---------- */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleSort = (col) => {
        if (!col.field) return;
        setSortConfig((prev) =>
            prev.key === col.field
                ? { ...prev, direction: prev.direction === "ascending" ? "descending" : "ascending" }
                : { key: col.field, direction: "ascending" }
        );
    };

    /* ---------- dnd ---------- */
    const dragOver = (e) => e.preventDefault();
    const drop = (e) => {
        onDropStatus?.(e.dataTransfer.getData("text/plain"));
    };

    return (
        <Paper sx={{ height, display: "flex", flexDirection: "column" }}>
            {/* header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ p: 2 }}>
                    {title}
                </Typography>
                <IconButton
                    sx={{ mr: 2 }}
                    onClick={() => handleChangeClickedTable?.(whatTable)}
                >
                    {clickedTable === whatTable ? (
                        <CloseFullscreenIcon sx={{ color: "#304FFF" }} />
                    ) : (
                        <OpenInFullIcon sx={{ color: "#304FFF" }} />
                    )}
                </IconButton>
            </Box>

            {/* table */}
            <TableContainer
                component={Paper}
                sx={{ height, overflowY: "auto" }}
                ref={tableRef}
                onDragOver={dragOver}
                onDrop={drop}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.label}
                                    sx={{
                                        backgroundColor: "#BFC8FF",
                                        position: "sticky",
                                        top: 0,
                                        color: "#304FFF",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textAlign: "center",
                                        cursor: col.field ? "pointer" : "default",
                                        padding: "8px 4px",
                                        minWidth: "80px",
                                    }}
                                    onClick={() => handleSort(col)}
                                >
                                    {col.label}
                                    {sortConfig.key === col.field &&
                                        (sortConfig.direction === "ascending" ? " ▲" : " ▼")}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginated.length ? (
                            paginated.map((row) => (
                                <TableRow
                                    key={row.id}
                                    sx={{ height: rowHeight }}
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData("text/plain", row.id)}
                                >
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.field || col.label}
                                            onClick={() => onRowClick?.(row)}
                                            sx={{
                                                padding: "4px 4px",
                                                fontSize: "12px",
                                                textAlign: "center",
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                maxWidth: "50px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {col.render ? col.render(row) : row[col.field]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow sx={{ height: rowHeight }}>
                                <TableCell colSpan={columns.length} align="center">
                                    {noDataMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* pagination */}
            <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('RowsPerPage')}
                rowsPerPageOptions={rowsPerPageOptions}
            />
        </Paper>
    );
}

export default React.memo(GenericTable);
